import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { PayloadRisk, RiskColor, RiskType } from '../../types';
import { RootState } from '../store';
import { EmotionScores } from './assistantDto';

interface AssistantState {
  riskOfChats: {
    [chatId: string]: {
      risk?: RiskType;
      reasoning_trace?: string | null;
      clientMessages?: number;
    };
  };

  // TODO: replace `content` with message UUID
  riskOfMessages: {
    [content: string]: {
      risk?: RiskType;
      reasoning_trace?: string | null;
      riskProcessing?: boolean;
    };
  };
  emotionsOfMessages: { [messageId: string]: EmotionScores };
}

const initialState: AssistantState = {
  riskOfChats: {},
  riskOfMessages: {},
  emotionsOfMessages: {},
};

export const assistantSlice = createSlice({
  name: 'assistant',
  initialState,
  reducers: {
    addRiskOfChat: (state, action: PayloadAction<PayloadRisk>) => {
      const { key, risk, reasoning_trace, clientMessages } = action.payload;
      state.riskOfChats[key] = {
        risk: risk,
        reasoning_trace: reasoning_trace,
        clientMessages: clientMessages,
      };
    },
    deleteRiskOfChat: (state, action: PayloadAction<string>) => {
      const key = action.payload;
      delete state.riskOfChats[key];
    },
    addRiskOfMessage: (state, action: PayloadAction<PayloadRisk>) => {
      const { key } = action.payload;
      state.riskOfMessages[key] = action.payload;
    },
    addEmotionsOfMessage: (
      state,
      action: PayloadAction<{ messageId: string; scores: EmotionScores }>
    ) => {
      const { messageId, scores } = action.payload;
      state.emotionsOfMessages[messageId] = scores;
    },
  },
});

export const {
  addRiskOfChat,
  deleteRiskOfChat,
  addRiskOfMessage,
  addEmotionsOfMessage,
} = assistantSlice.actions;

export const selectRiskOfChats = (state: RootState) =>
  state.assistant.riskOfChats;

export const selectRiskOfMessages = (state: RootState) =>
  state.assistant.riskOfMessages;

export const riskToLabel = (
  risk: RiskType
): { content: string; color: RiskColor } => {
  switch (risk) {
    case 'Low':
      return { content: 'Low risk', color: 'secondary' };
    case 'Medium':
      return { content: 'Medium risk', color: 'warning' };
    case 'High':
      return { content: 'High risk', color: 'danger' };
    case 'Neutral':
      return { content: 'Neutral', color: 'default' };
    default:
      return { content: 'Unknown risk', color: 'default' };
  }
};

export const selectEmotionsOfMessages = (state: RootState) =>
  state.assistant.emotionsOfMessages;

export default assistantSlice.reducer;
